import React, { useState, useContext, useEffect } from 'react';
import { AuthContext, useAPI } from 'components/lib';
import styles from './styles.module.css';

export function Rules(props){

  const authContext = useContext(AuthContext);
  
  const [rules, setRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filteredRules, setFilteredRules] = useState([]);
  const list = useAPI('/api/auditRule/list');
  
  useEffect(() => {
    if(list?.data?.length) {
      setRules(list.data);
      setFilteredRules(list.data);
    }
  }, [list.data]);

  useEffect(() => {
    let filtered = rules;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(rule => 
        rule.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.short_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.record_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(rule => 
        filterType === 'active' ? rule.is_active : !rule.is_active
      );
    }
    
    setFilteredRules(filtered);
  }, [searchTerm, filterType, rules]);

  function editRule(data, callback){
    window.location = "ruleDetails/" + data.rule_id;
  }

  const getRuleTypeClass = (type) => {
    switch(type) {
      case 'info': return 'ruleTypeInfo';
      case 'warning': return 'ruleTypeWarning';
      case 'error': return 'ruleTypeError';
      default: return 'ruleTypeInfo';
    }
  };

  const formatRecordType = (type) => {
    if (!type) return '';
    // Replace single uppercase letters with space, but keep consecutive uppercase letters together
    return type.charAt(0).toUpperCase() +
      type.slice(1).replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // handle transitions like "ABc"
      .replace(/([a-z])([A-Z]{2,})/g, '$1 $2') // handle transitions like "aAB"
      .replace(/([a-z])([A-Z])/g, '$1 $2'); // handle single uppercase transitions
  };

  return (
    <div className={styles.rulesListContainer}>
      <div className={styles.rulesListHeader}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
          <div>
            <h1 className={styles.rulesListTitle}>Rule Monitors</h1>
            <p className={styles.rulesListSubtitle}>
              Manage and monitor your data validation rules. Create custom rules to ensure data quality and get notified when issues arise.
            </p>
          </div>
          {authContext.hasAccountSetting("edit_rules") === true && (
            <button 
              className={styles.createRuleBtn}
              onClick={() => window.location = "ruledetails"}
            >
              <i className="pi pi-plus" style={{marginRight: '8px'}}></i>
              Create New Monitor
            </button>
          )}
        </div>
        
        {/* Search and Filter Bar */}
        <div style={{display: 'flex', gap: '12px', marginTop: '20px', alignItems: 'center'}}>
          <div style={{flex: 1, position: 'relative', maxWidth: 'none'}}>
            <i className="pi pi-search" style={{
              position: 'absolute', 
              left: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#6b7280',
              fontSize: '14px'
            }}></i>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search rules by name, description, or record type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterSelect}
            style={{minWidth: '140px', width: '140px'}}
          >
            <option value="all">All Rules</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Rules Grid */}
      <div style={{display: 'grid', gap: '6px'}}>
        {filteredRules.length > 0 ? (
          filteredRules.map((rule, index) => (
            <div 
              key={rule.rule_id}
              className={`${styles.ruleCardCompact} ${rule.is_active ? styles.ruleCardActive : styles.ruleCardInactive}`}
              onClick={() => editRule(rule)}
            >
              <div className={styles.ruleCardCompactTitle}>{rule.display_name}</div>
              <div className={styles.ruleCardCompactDescription}>{rule.short_description}</div>
              
              <div className={styles.ruleCardCompactMeta}>
                <span className={`${styles.ruleCardType} ${styles[getRuleTypeClass(rule.failure_type)]}`}>
                  {rule.failure_type || 'info'}
                </span>
                <span className={styles.ruleCardCompactRecordType}>
                  {formatRecordType(rule.record_type)}
                </span>
                <span className={`${styles.statusBadge} ${rule.is_active ? styles.statusActive : styles.statusInactive}`}>
                  {rule.is_active ? 'Active' : 'Inactive'}
                </span>
                <i className="pi pi-chevron-right" style={{color: '#6b7280', fontSize: '12px'}}></i>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            {searchTerm || filterType !== 'all' ? (
              <>
                <i className={`pi pi-search ${styles.emptyStateIcon}`}></i>
                <h3 className={styles.emptyStateTitle}>No rules found</h3>
                <p className={styles.emptyStateText}>Try adjusting your search or filter criteria</p>
              </>
            ) : (
              <>
                <i className={`pi pi-shield ${styles.emptyStateIcon}`}></i>
                <h3 className={styles.emptyStateTitle}>No rules created yet</h3>
                <p className={styles.emptyStateText}>Create your first rule monitor to start validating your data</p>
                {authContext.hasAccountSetting("edit_rules") === true && (
                  <button 
                    className={styles.createRuleBtn}
                    onClick={() => window.location = "ruledetails"}
                    style={{marginTop: '12px'}}
                  >
                    Create Your First Rule
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {rules.length > 0 && (
        <div className={styles.statsBar}>
          <span><strong>{rules.length}</strong> Total Rules</span>
          <span><strong>{rules.filter(r => r.is_active).length}</strong> Active</span>
          <span><strong>{rules.filter(r => !r.is_active).length}</strong> Inactive</span>
        </div>
      )}
    </div>
  );
}
